import React from 'react';
import {
  faExternalLinkAlt,
  faPeopleGroup,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { Typography } from 'src/components';
import { getSocTypeIcon } from 'src/components/TypeIcon/TypeIcon';
import { profileTypeToSlug } from 'src/utils/profileUrl';
import Link from 'next/link';

interface AffiliationItem {
  id: string;
  name: string;
  type: string;
  soc_type?: string;
}

interface AffiliationSectionProps {
  title: string;
  items?: AffiliationItem[];
  baseUrl?: string;
  testId?: string;
  /**
   * When true (default) each item links to its profile with a type icon. When false the
   * items render as plain, non-interactive text with no icons — used for affiliations that
   * have no profile page or icon (e.g. GPO, EHR Provider).
   */
  isLink?: boolean;
}

/**
 * Reusable component for displaying affiliation sections (Systems, Groups, Hospitals)
 * with consistent styling and link behavior
 */
const AffiliationSection: React.FC<AffiliationSectionProps> = ({
  title,
  items = [],
  baseUrl = '/profiles',
  testId,
  isLink = true,
}) => {
  const renderIcon = (item: AffiliationItem) => {
    const icon =
      item.type === 'group_pac'
        ? faPeopleGroup
        : getSocTypeIcon(item.soc_type || item.type);
    return <Icon icon={icon} className="w-3.5 h-3 text-link" />;
  };

  return (
    <div 
      className="self-stretch inline-flex flex-col justify-center items-start gap-1"
      data-testid={testId}
    >
      <div className="justify-start text-tertiary text-sm font-normal font-['Inter'] leading-tight">
        {title}
      </div>
      {items.length > 0 ? (
        items.map((item, index) =>
          isLink ? (
            <Link
              key={`${item.id}-${index}`}
              href={`${baseUrl}/${profileTypeToSlug(item.type)}/${item.id}`}
              className="self-stretch inline-flex justify-start items-center gap-1"
              scroll={false}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                {renderIcon(item)}
              </div>
              <div className="justify-start text-link text-sm font-semibold font-['Inter'] leading-tight">
                {item.name}
              </div>
              <div className="w-3 h-3 flex items-center justify-center">
                <Icon icon={faExternalLinkAlt} className="w-3 h-3 text-link" />
              </div>
            </Link>
          ) : (
            <div
              key={`${item.id}-${index}`}
              className="self-stretch inline-flex justify-start items-center gap-1"
            >
              <div className="justify-start text-primary text-sm font-semibold font-['Inter'] leading-tight">
                {item.name}
              </div>
            </div>
          )
        )
      ) : (
        <Typography className="font-medium text-sm text-primary">
          Not available
        </Typography>
      )}
    </div>
  );
};

export default AffiliationSection;
